import { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface ChatMessage {
  id: string;
  body: string;
  from: 'visitor' | 'agent';
  at: string;
}

export function WebchatWidget() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [intro, setIntro] = useState(false);
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const sessionRef = useRef<string | null>(null);

  const startMut = useMutation({
    mutationFn: () =>
      api.post<{ sessionId: string }>(`/public/webchat/${tenantSlug}/start`, { name, email }),
    onSuccess: (r) => {
      sessionRef.current = r.sessionId;
      setIntro(true);
      setMessages([
        { id: 'sys', body: `Hi ${name}, how can we help?`, from: 'agent', at: new Date().toISOString() },
      ]);
    },
  });

  const sendMut = useMutation({
    mutationFn: () =>
      api.post(`/public/webchat/${tenantSlug}/send`, { sessionId: sessionRef.current, body: draft }),
    onSuccess: () => {
      setMessages((m) => [...m, { id: String(Date.now()), body: draft, from: 'visitor', at: new Date().toISOString() }]);
      setDraft('');
    },
  });

  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (containerRef.current) containerRef.current.scrollTop = containerRef.current.scrollHeight;
  }, [messages]);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: '#3b82f6',
          color: 'white',
          border: 'none',
          fontSize: 24,
          cursor: 'pointer',
          boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        }}
        aria-label="Open chat"
      >
        💬
      </button>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        width: 360,
        maxWidth: 'calc(100vw - 32px)',
        height: 480,
        background: 'white',
        borderRadius: 12,
        boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'system-ui, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div style={{ background: '#3b82f6', color: 'white', padding: 12, fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Chat with us</span>
        <button onClick={() => setOpen(false)} style={{ background: 'transparent', color: 'white', border: 'none', fontSize: 18, cursor: 'pointer' }} aria-label="Close">×</button>
      </div>
      {!intro ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!name || !email) return;
            startMut.mutate();
          }}
          style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}
        >
          <p style={{ fontSize: 14, margin: 0 }}>Tell us a bit about yourself.</p>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" style={inputStyle} />
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email" style={inputStyle} />
          <button type="submit" disabled={startMut.isPending} style={btnStyle}>
            {startMut.isPending ? 'Starting…' : 'Start chat'}
          </button>
        </form>
      ) : (
        <>
          <div ref={containerRef} style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {messages.map((m) => (
              <div
                key={m.id}
                style={{
                  alignSelf: m.from === 'visitor' ? 'flex-end' : 'flex-start',
                  background: m.from === 'visitor' ? '#3b82f6' : '#f1f5f9',
                  color: m.from === 'visitor' ? 'white' : '#1e293b',
                  padding: '8px 12px',
                  borderRadius: 12,
                  maxWidth: '80%',
                  fontSize: 14,
                }}
              >
                {m.body}
              </div>
            ))}
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!draft.trim()) return;
              sendMut.mutate();
            }}
            style={{ display: 'flex', gap: 4, padding: 8, borderTop: '1px solid #e2e8f0' }}
          >
            <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Type a message…" style={{ ...inputStyle, flex: 1 }} />
            <button type="submit" disabled={sendMut.isPending} style={btnStyle}>Send</button>
          </form>
        </>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  fontSize: 14,
  outline: 'none',
};

const btnStyle: React.CSSProperties = {
  background: '#3b82f6',
  color: 'white',
  border: 'none',
  padding: '8px 14px',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 14,
};
